import { XIMI_MOBILE_API_URL } from '../../config';
import { ximiUserManager } from '../../utilities/ximi/oidcConfig';
import { getCurrentXimiAccount } from '../../utilities/ximi/storage/ximiAccounts';
import { getXimiDeviceContext } from './ximiDevice';
import { postXimiJson, getXimiJson, XimiHttpResponse } from './ximiHttp';

/** Headers communs : Bearer (token COURANT, lu à chaque appel) + identité device (mémoïsée). */
async function buildXimiMobileHeaders(): Promise<Record<string, string>> {
    const user = await ximiUserManager.getUser();
    const device = await getXimiDeviceContext();
    return {
        Authorization: `Bearer ${user?.access_token ?? ''}`,
        uuid: device.uuid,
        applicationversion: device.applicationversion
    };
}

/**
 * URL MobileAPI : base + préfixe app + /api/ + suffixe (forme QA, alignée Ximi Famille).
 * Le préfixe est la SEULE partie app du tenantCode (`appId`), points convertis en slashes ;
 * le segment tenant (ex. `xmob`) est exclu — il est résolu côté serveur via le claim
 * `xelya.provider` du token, pas via l'URL. Ex. `Master.XimiIndep/xmob` → `Master/XimiIndep`.
 * (cf. Ximi Famille `tenant.service.ts` `firstPartTenant` : `.replace(/\./g, '/')`.)
 */
async function buildXimiMobileUrl(suffix: string): Promise<string> {
    const account = await getCurrentXimiAccount();
    if (!account) throw new Error('No current Ximi account');
    // appId désormais stocké (fallback split pour comptes legacy) ; points → slashes (forme URL).
    const appId = account.appId ?? account.tenantCode.split('/')[0];
    return `${XIMI_MOBILE_API_URL}/${appId.replace(/\./g, '/')}/api/${suffix}`;
}

/** POST MobileAPI : préfixe (base + tenant + /api/) + injecte token & headers device. */
export async function ximiMobilePost<T>(
    suffix: string,
    body: unknown
): Promise<XimiHttpResponse<T>> {
    return postXimiJson<T>(await buildXimiMobileUrl(suffix), body, await buildXimiMobileHeaders());
}

/** GET MobileAPI : préfixe (base + tenant + /api/) + injecte token & headers device. */
export async function ximiMobileGet<T>(suffix: string): Promise<XimiHttpResponse<T>> {
    return getXimiJson<T>(await buildXimiMobileUrl(suffix), await buildXimiMobileHeaders());
}

/**
 * GET MobileAPI SANS préfixe app/tenant : `<base>/api/<suffix>`. Le tenant est résolu
 * côté serveur via le claim `xelya.provider` du token (cas de l'endpoint Stimul'In
 * `api/stimulin/identity`). Ne dépend donc PAS d'un compte Ximi courant stocké.
 */
export async function ximiMobileGetFlat<T>(suffix: string): Promise<XimiHttpResponse<T>> {
    return getXimiJson<T>(`${XIMI_MOBILE_API_URL}/api/${suffix}`, await buildXimiMobileHeaders());
}
